import type { JSX } from "preact/jsx-runtime";
import type {
	BrickPosition,
	NumberOfBricks,
} from "@brickme/project-core/src/model/picture.ts";
import type { PixelPosition } from "@brickme/project-core/src/model/geom.ts";

type MouseOrTouchCanvasEvent =
	| JSX.TargetedMouseEvent<HTMLCanvasElement>
	| JSX.TargetedTouchEvent<HTMLCanvasElement>;

function getCanvasPixelPosition(
	e: MouseOrTouchCanvasEvent,
): PixelPosition | undefined {
	const touches: readonly { clientX: number; clientY: number }[] =
		"clientX" in e
			? [e]
			: Array.from({ length: e.touches.length }, (_, i) => e.touches[i]);

	// This happens for ontouchend. But also good to have it as a safeguard in case occurs elsewhere.
	if (touches.length === 0) {
		return undefined;
	}

	const clientX = touches.reduce((a, t) => a + t.clientX, 0) / touches.length;
	const clientY = touches.reduce((a, t) => a + t.clientY, 0) / touches.length;
	const rect = e.currentTarget.getBoundingClientRect();
	return { x: clientX - rect.left, y: clientY - rect.top };
}

function canvasPositionToBrick(
	canvas: HTMLCanvasElement,
	pixelPosition: PixelPosition,
	numberOfBricks: NumberOfBricks,
): BrickPosition {
	const bounding = canvas.getBoundingClientRect();
	const xRatio = pixelPosition.x / bounding.width;
	const yRatio = pixelPosition.y / bounding.height;
	const x = Math.min(
		Math.floor(xRatio * numberOfBricks.width),
		numberOfBricks.width - 1,
	);
	const y = Math.min(
		Math.floor(yRatio * numberOfBricks.height),
		numberOfBricks.height - 1,
	);
	return { x, y };
}

export type { MouseOrTouchCanvasEvent };
export { getCanvasPixelPosition, canvasPositionToBrick };
